import { PartyData } from '@types';
import { Card } from '_atoms';
import React, { useState } from 'react';
import { MdArrowBack, MdArrowForward } from 'react-icons/md';

interface AdditionalDetailsProps {
  additionalDetails?: {
    guestName: string;
    notes: string;
  };
  onNext: (data?: Partial<PartyData>) => void;
  onPrevious: () => void;
  onStepComplete: (data: Partial<PartyData>) => void;
  notesReadOnly?: boolean;
}

export const AdditionalDetails: React.FC<AdditionalDetailsProps> = ({
  additionalDetails,
  onNext,
  onPrevious,
  onStepComplete,
  notesReadOnly = false,
}) => {
  const [guestName, setGuestName] = useState(
    additionalDetails?.guestName || '',
  );
  const [notes, setNotes] = useState(additionalDetails?.notes || '');

  const handleContinue = () => {
    const data: Partial<PartyData> = {
      additionalDetails: {
        guestName,
        // Preserve original customer notes when read-only
        notes: notesReadOnly ? additionalDetails?.notes || '' : notes,
      },
    };

    onStepComplete(data);
    onNext(data);
  };

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold text-gray-900">Additional Details</h2>
        <p className="text-gray-600 text-lg">
          Tell us more about your party (optional)
        </p>
      </div>

      <Card>
        <div className="space-y-6">
          {/* Guest Name Field */}
          <div>
            <label
              htmlFor="guestName"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              Name of Birthday Guest, Guest of Honor, or Group
            </label>
            <input
              type="text"
              id="guestName"
              value={guestName}
              onChange={e => setGuestName(e.target.value)}
              placeholder="Enter the name of the guest of honor"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            />
            <p className="mt-1 text-sm text-gray-500">
              e.g., Birthday child's name or event honoree
            </p>
          </div>

          {/* Notes Field */}
          <div>
            <label
              htmlFor="notes"
              className="block text-sm font-medium text-gray-700 mb-2"
            >
              {notesReadOnly
                ? 'Cust. Notes'
                : 'Any information we should know?'}
            </label>
            <textarea
              id="notes"
              value={notes}
              onChange={e => {
                if (!notesReadOnly) setNotes(e.target.value);
              }}
              placeholder={
                notesReadOnly
                  ? 'No customer notes entered'
                  : 'Any information we should know?'
              }
              rows={5}
              readOnly={notesReadOnly}
              disabled={notesReadOnly}
              className={`w-full px-4 py-3 border border-gray-300 rounded-lg resize-none transition-all ${
                notesReadOnly
                  ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                  : 'focus:ring-2 focus:ring-blue-500 focus:border-transparent'
              }`}
            />
            {notesReadOnly ? (
              <p className="mt-1 text-sm text-gray-500">
                Customer notes cannot be changed. Use Staff Notes to add
                internal comments.
              </p>
            ) : (
              <p className="mt-1 text-sm text-gray-500"></p>
            )}
          </div>
        </div>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between gap-4 pt-6">
        <button
          onClick={onPrevious}
          className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <MdArrowBack className="text-xl" />
          <span>Previous</span>
        </button>

        <button
          onClick={handleContinue}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <span>Continue</span>
          <MdArrowForward className="text-xl" />
        </button>
      </div>
    </div>
  );
};
